import React from "react";
import {
  APIProvider,
  Map as GoogleMap,
  Marker,
} from "@vis.gl/react-google-maps";
import { Avatar, Sheet } from "@mui/joy";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useGetGooglePlaceDetails } from "../api/use-get-google-place-details";
import { useAuthStore } from "../store/authStore";
import { MapToolbar } from "./MapToolbar";
import { useMapState, MapState } from "../hooks/useMapState";
import { debounce } from "lodash";

export const Map = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const mapState: MapState = useMapState();

  // Read initial list params from URL (e.g., from "View on Map" on profile)
  const initialListId = React.useRef(searchParams.get("listId"));
  const initialListMode = React.useRef(
    searchParams.get("mode") as "edit" | "view" | null,
  );

  // Clear search params after first read
  React.useEffect(() => {
    if (searchParams.has("listId")) {
      setSearchParams({}, { replace: true });
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const {
    mapPlaceId,
    setMapPlaceId,
    mapCenter,
    setMapCenter,
    zoom,
    setZoom,
  } = mapState;

  const { data: placeDetails } = useGetGooglePlaceDetails(mapPlaceId);

  const { profile } = useAuthStore();

  React.useEffect(() => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition((position) => {
        setMapCenter({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
      });
    }
  }, [setMapCenter]);

  React.useEffect(() => {
    if (placeDetails) {
      setZoom(16);
      setMapCenter({
        lat: placeDetails.location.latitude,
        lng: placeDetails.location.longitude,
      });
    }
  }, [placeDetails, setMapCenter, setZoom]);

  const debouncedSetCenter = React.useMemo(
    // 0ms delay but seems to improve performance
    () => debounce((center: { lat: number; lng: number }) => setMapCenter(center), 0),
    [setMapCenter],
  );

  const debouncedSetZoom = React.useMemo(
    () => debounce((zoom: number) => setZoom(zoom), 5),
    [setZoom],
  );

  const debouncedOnClick = React.useMemo(
    () => debounce((placeId: string | null) => {
      // Only set mapPlaceId - the side panel will open automatically
      setMapPlaceId(placeId);
    }, 5),
    [setMapPlaceId],
  );
  return (
    <Sheet sx={{
      height: "100dvh",
      "@supports not (height: 100dvh)": { height: "100vh" },
      display: "flex",
      position: "relative",
    }}>
      <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ""}>
        <GoogleMap
          disableDefaultUI
          zoomControl
          center={mapCenter}
          zoom={zoom}
          onCenterChanged={(event) => debouncedSetCenter(event.detail.center)}
          onZoomChanged={(event) => debouncedSetZoom(event.detail.zoom)}
          onClick={(event) => debouncedOnClick(event.detail.placeId)}
        >
          {placeDetails && (
            <Marker
              position={{
                lat: placeDetails.location.latitude,
                lng: placeDetails.location.longitude,
              }}
            />
          )}
        </GoogleMap>
      </APIProvider>

      <MapToolbar
        onSelectPlace={(place) => {
          // Set the map place ID - the side panel will open automatically
          setMapPlaceId(place ? place.placeId : null);
        }}
        mapState={mapState}
        initialListId={initialListId.current}
        initialListMode={initialListMode.current}
      />

      {/* Profile Bubble */}
      <Avatar
        src={profile?.avatar_url || undefined}
        onClick={() => navigate('/profile')}
        sx={{
          position: "absolute",
          top: 16,
          right: 16,
          width: { xs: 40, sm: 48 },
          height: { xs: 40, sm: 48 },
          cursor: "pointer",
          boxShadow: "md",
          border: "2px solid white",
          zIndex: 100,
          "&:hover": {
            transform: "scale(1.05)",
          },
          transition: "transform 0.2s",
        }}
      >
        {profile?.full_name?.charAt(0) || profile?.username?.charAt(0) || "U"}
      </Avatar>

    </Sheet>
  );
};
